import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Flame, Diamond, Heart, Zap, TrendingUp, Users, Sparkles, MessageSquare, BookOpen, Palette, Music, X, RotateCcw, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Avatar } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import analytics from '../services/analytics';
import { playReactionAnimation } from '../utils/reactionAnimations';
import PostComposer from '../components/PostComposer';
import PostCard from '../components/PostCard';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

// Available genres
const AVAILABLE_GENRES = [
  'Action', 'Adventure', 'Comedy', 'Drama', 'Fantasy', 'Horror',
  'Romance', 'Sci-Fi', 'Slice of Life', 'Thriller', 'Mystery',
  'Historical', 'Sports', 'Supernatural', 'Urban', 'Music'
];

// Content types
const CONTENT_TYPES = [
  { id: 'posts', label: 'Posts', icon: MessageSquare },
  { id: 'comic', label: 'Comics', icon: BookOpen },
  { id: 'art', label: 'Artwork', icon: Palette },
  { id: 'music', label: 'Music', icon: Music }
];

const Feed = () => {
  const { user } = useAuth();
  const [personalizedFeed, setPersonalizedFeed] = useState([]);
  const [discoveryFeed, setDiscoveryFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [activeTab, setActiveTab] = useState('following');
  
  // Filter states
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState(['posts', 'comic', 'art', 'music']);
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const [filterTrackTimeout, setFilterTrackTimeout] = useState(null);
  
  // Ref to prevent duplicate fetches
  const hasFetchedPrefs = useRef(false);

  // Load preferences on mount - only once
  useEffect(() => {
    if (user?.id && !hasFetchedPrefs.current) {
      hasFetchedPrefs.current = true;
      loadPreferences();
    } else if (!user) {
      // No user, just set loading to false
      setLoadingPreferences(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Fetch feed when filters or tab changes
  useEffect(() => {
    if (!loadingPreferences) {
      if (user && activeTab === 'following') {
        fetchPersonalizedFeed();
      }
      if (activeTab === 'discover') {
        fetchDiscoveryFeed();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedGenres, selectedTypes, loadingPreferences]);

  const loadPreferences = async () => {
    try {
      // Load from localStorage first (fast)
      const localGenres = localStorage.getItem('feed_genres');
      const localTypes = localStorage.getItem('feed_types');
      
      if (localGenres) setSelectedGenres(JSON.parse(localGenres));
      if (localTypes) setSelectedTypes(JSON.parse(localTypes));

      // If logged in, sync with database (fail-soft)
      if (user) {
        const token = localStorage.getItem('token');
        try {
          const response = await axios.get(`${API}/user/preferences`, {
            headers: { 'Authorization': `Bearer ${token}` },
            timeout: 5000
          });

          // Merge preferences (DB is source of truth)
          const dbTypes = response.data.content_type_preferences || ['posts', 'comic', 'art', 'music'];
          setSelectedTypes(dbTypes);
          localStorage.setItem('feed_types', JSON.stringify(dbTypes));

          // For genres, we'll keep them simple for now (not per-content-type)
          const allGenres = [
            ...(response.data.genre_preferences?.comics || []),
            ...(response.data.genre_preferences?.art || []),
            ...(response.data.genre_preferences?.music || [])
          ];
          const uniqueGenres = [...new Set(allGenres)];
          if (uniqueGenres.length > 0) {
            setSelectedGenres(uniqueGenres);
            localStorage.setItem('feed_genres', JSON.stringify(uniqueGenres));
          }
        } catch (prefError) {
          // Fail soft - use localStorage values
          console.warn('Preferences fetch failed, using localStorage:', prefError.message);
        }
      }
    } catch (error) {
      console.warn('Error loading preferences:', error.message);
    } finally {
      setLoadingPreferences(false);
    }
  };

  const savePreferences = async (genres, types) => {
    try {
      // Save to localStorage immediately
      localStorage.setItem('feed_genres', JSON.stringify(genres));
      localStorage.setItem('feed_types', JSON.stringify(types));

      // If logged in, save to database (fail-soft)
      if (user) {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/user/preferences`, {
          genre_preferences: {
            comics: genres,
            art: genres,
            music: genres
          },
          content_type_preferences: types
        }, {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 5000
        }).catch(err => {
          console.warn('Preferences save failed:', err.message);
        });
      }
    } catch (error) {
      console.warn('Error saving preferences:', error.message);
      // Don't show error toast - localStorage save succeeded
    }
  };

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', 20);
    
    if (selectedGenres.length > 0) {
      params.append('genres', selectedGenres.join(','));
    }
    
    if (selectedTypes.length > 0 && selectedTypes.length < 3) {
      params.append('types', selectedTypes.join(','));
    }
    
    return params.toString();
  };

  const fetchPersonalizedFeed = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/feed?${buildQueryParams()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setPersonalizedFeed(response.data.feed);
      setHasMore(response.data.has_more);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching feed:', error);
      setLoading(false);
    }
  };

  const fetchDiscoveryFeed = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/feed/discover?${buildQueryParams()}`);
      setDiscoveryFeed(response.data.feed);
      setHasMore(response.data.has_more);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching discovery feed:', error);
      setLoading(false);
    }
  };

  const toggleGenre = (genre) => {
    const newGenres = selectedGenres.includes(genre)
      ? selectedGenres.filter(g => g !== genre)
      : [...selectedGenres, genre].slice(0, 8);  // Max 8 genres
    
    setSelectedGenres(newGenres);
    savePreferences(newGenres, selectedTypes);
    setPage(1);  // Reset to page 1
    
    // Debounced tracking (wait 1 second after last change)
    if (filterTrackTimeout) clearTimeout(filterTrackTimeout);
    const timeout = setTimeout(() => {
      analytics.trackFilterChange(newGenres, selectedTypes, activeTab);
    }, 1000);
    setFilterTrackTimeout(timeout);
  };

  const toggleContentType = (typeId) => {
    const newTypes = selectedTypes.includes(typeId)
      ? selectedTypes.filter(t => t !== typeId)
      : [...selectedTypes, typeId];
    
    // Don't allow empty selection
    if (newTypes.length === 0) {
      toast.error('Please select at least one content type');
      return;
    }
    
    setSelectedTypes(newTypes);
    savePreferences(selectedGenres, newTypes);
    setPage(1);  // Reset to page 1
    
    // Debounced tracking (wait 1 second after last change)
    if (filterTrackTimeout) clearTimeout(filterTrackTimeout);
    const timeout = setTimeout(() => {
      analytics.trackFilterChange(selectedGenres, newTypes, activeTab);
    }, 1000);
    setFilterTrackTimeout(timeout);
  };

  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedTypes(['comic', 'art', 'music']);
    savePreferences([], ['comic', 'art', 'music']);
    setPage(1);
  };

  const [userReactions, setUserReactions] = useState({});
  const [reactingPost, setReactingPost] = useState(null);
  const pendingFeedBtnRef = useRef(null);

  const handleReaction = async (postId, reactionType) => {
    if (!user) {
      toast.error('Please sign in to react');
      return;
    }
    if (reactingPost === postId) return;

    setReactingPost(postId);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('target_type', 'fan_wall_post');
      formData.append('target_id', postId);
      formData.append('reaction_type', reactionType);

      const res = await axios.post(`${API}/react`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      
      analytics.trackReaction('fan_wall_post', postId, reactionType, res.data.action);
      setUserReactions(prev => ({ ...prev, [postId]: res.data.reaction_type }));

      // Animate only on add/switch
      if ((res.data.action === 'added' || res.data.action === 'updated') && pendingFeedBtnRef.current) {
        playReactionAnimation(pendingFeedBtnRef.current, reactionType);
      }
      pendingFeedBtnRef.current = null;

      const updateFeed = (feed) => feed.map(post => {
        if (post.id === postId) {
          return { ...post, reactions: res.data.counts };
        }
        return post;
      });

      if (activeTab === 'following') {
        setPersonalizedFeed(updateFeed(personalizedFeed));
      } else {
        setDiscoveryFeed(updateFeed(discoveryFeed));
      }

    } catch (error) {
      console.error('Error adding reaction:', error);
      toast.error(error.response?.data?.detail || 'Failed to add reaction');
    } finally {
      setReactingPost(null);
    }
  };

  const getContentIcon = (postType) => {
    switch (postType) {
      case 'artwork_drop':
        return <Palette className="w-4 h-4" />;
      case 'episode_drop':
        return <BookOpen className="w-4 h-4" />;
      case 'music_drop':
        return <Music className="w-4 h-4" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getContentBadge = (postType) => {
    const badges = {
      'artwork_drop': { text: 'New Artwork', color: 'from-purple-500 to-pink-500' },
      'episode_drop': { text: 'New Episode', color: 'from-orange-500 to-red-500' },
      'music_drop': { text: 'New Track', color: 'from-cyan-500 to-blue-500' },
      'creator_post': { text: 'Status Update', color: 'from-blue-500 to-indigo-500' },
      'fan_wall_message': { text: 'Post', color: 'from-gray-600 to-gray-700' }
    };
    return badges[postType] || badges['fan_wall_message'];
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const FeedPost = ({ post, position }) => {
    const badge = getContentBadge(post.feed_type);
    
    const handleContentClick = (destination) => {
      analytics.trackFeedClick(post.id, destination);
    };
    
    return (
      <Card className="bg-gray-900 border-gray-800 hover:border-gray-700 transition-colors">
        <CardContent className="p-4 sm:p-6">
          {/* Post Header */}
          <div className="flex items-start gap-3 mb-4">
            <Link to={`/u/${post.author_profile?.handle}`}>
              <Avatar className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800">
                {post.author_profile?.avatar_url ? (
                  <img src={post.author_profile.avatar_url} alt={post.author_profile.display_name} />
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-gray-400">
                    {post.author_profile?.display_name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </Avatar>
            </Link>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={`/u/${post.author_profile?.handle}`} className="font-bold text-white hover:text-orange-500 transition-colors">
                  {post.author_profile?.display_name}
                </Link>
                <span className="text-gray-500">@{post.author_profile?.handle}</span>
                {post.author_profile?.creator_tier && (
                  <Badge className="bg-orange-500/10 text-orange-500 text-xs">
                    {post.author_profile.creator_tier}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400 mt-1">
                <Badge className={`bg-gradient-to-r ${badge.color} text-white text-xs`}>
                  {getContentIcon(post.feed_type)}
                  <span className="ml-1">{badge.text}</span>
                </Badge>
                <span>•</span>
                <span>{formatTimeAgo(post.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Post Content */}
          <div className="mb-4">
            <p className="text-white whitespace-pre-wrap">{post.message}</p>
          </div>

          {/* Post Image */}
          {post.image_url && (
            <div className="mb-4">
              <img
                src={post.image_url}
                alt="Post content"
                className="rounded-lg max-h-96 w-full object-cover"
              />
            </div>
          )}

          {/* Linked Content */}
          {post.linked_content && (
            <div className="mb-4">
              <Link
                to={
                  post.linked_content.type === 'chapter'
                    ? `/series/${post.linked_content.series_id}/episode/${post.linked_content.id}`
                    : post.linked_content.type === 'art_post'
                    ? `/art/${post.linked_content.id}`
                    : '#'
                }
                onClick={() => handleContentClick(post.linked_content.type === 'chapter' ? 'episode' : 'art')}
                className="block bg-gray-800 hover:bg-gray-750 rounded-lg p-3 border border-gray-700 hover:border-orange-500 transition-colors"
              >
                <div className="flex items-center gap-2 text-orange-500">
                  {getContentIcon(post.feed_type)}
                  <span className="font-medium">View {post.feed_type === 'episode_drop' ? 'Episode' : 'Artwork'}</span>
                </div>
              </Link>
            </div>
          )}

          {/* Reactions */}
          <div className="flex items-center gap-3 pt-3 border-t border-gray-800" data-testid="feed-reaction-buttons">
            {[
              { type: 'heart', Icon: Heart, color: 'pink', label: 'Love' },
              { type: 'fire', Icon: Flame, color: 'orange', label: 'Fire' },
              { type: 'diamond', Icon: Diamond, color: 'cyan', label: 'Diamond' },
            ].map(({ type, Icon, color, label }) => {
              const isSelected = userReactions[post.id] === type;
              return (
                <button
                  key={type}
                  onClick={(e) => {
                    pendingFeedBtnRef.current = e.currentTarget;
                    handleReaction(post.id, type);
                  }}
                  disabled={reactingPost === post.id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50 ${
                    isSelected
                      ? `bg-${color}-500/20 text-${color}-400 ring-1 ring-${color}-500/40`
                      : `hover:bg-${color}-500/10 text-${color}-500`
                  }`}
                  data-testid={`feed-reaction-${type}-${post.id}`}
                  title={label}
                  aria-label={`${label}${isSelected ? ' (selected)' : ''}`}
                  aria-pressed={isSelected}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{post.reactions?.[type] || 0}</span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (!user && activeTab === 'following') {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="container mx-auto px-4 sm:px-6 py-12">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-12 text-center">
              <Sparkles className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-3">Sign in to see your Feed</h2>
              <p className="text-gray-400 mb-6">
                Follow creators to see their latest drops and updates here
              </p>
              <Link to="/auth">
                <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                  Sign In
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Feed</h1>
          <p className="text-gray-400">Stay updated with the latest drops from creators you follow</p>
        </div>

        {/* Filter Section */}
        <div className="mb-6 space-y-4">
          {/* Content Type Filters */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-400">Content Type</label>
              {(selectedGenres.length > 0 || selectedTypes.length < 3) && (
                <button
                  onClick={resetFilters}
                  className="text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1"
                >
                  <RotateCcw className="w-3 h-3" />
                  Reset All
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {CONTENT_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = selectedTypes.includes(type.id);
                return (
                  <button
                    key={type.id}
                    onClick={() => toggleContentType(type.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                      isSelected
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {type.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Genre Filters */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-2 block">
              Genres {selectedGenres.length > 0 && `(${selectedGenres.length}/8)`}
            </label>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_GENRES.map((genre) => {
                const isSelected = selectedGenres.includes(genre);
                return (
                  <button
                    key={genre}
                    onClick={() => toggleGenre(genre)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      isSelected
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {genre}
                    {isSelected && <X className="w-3 h-3 ml-1 inline" />}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">Filters apply to both tabs</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-gray-900 border border-gray-800 mb-6">
            <TabsTrigger value="following" className="data-[state=active]:bg-orange-500">
              <Users className="w-4 h-4 mr-2" />
              Following
            </TabsTrigger>
            <TabsTrigger value="discover" className="data-[state=active]:bg-orange-500">
              <TrendingUp className="w-4 h-4 mr-2" />
              Discover
            </TabsTrigger>
          </TabsList>

          {/* Post Composer - For creators only */}
          {user && (user.role === 'artist' || user.role === 'author' || user.role === 'admin') && activeTab === 'following' && (
            <div className="mb-6">
              <PostComposer 
                onPostCreated={(newPost) => {
                  // Refresh feed to show new post
                  fetchPersonalizedFeed();
                }}
              />
            </div>
          )}

          {/* Following Feed */}
          <TabsContent value="following">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : personalizedFeed.length === 0 ? (
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-12 text-center">
                  <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">
                    {selectedGenres.length > 0 || selectedTypes.length < 3
                      ? 'No posts match your filters'
                      : 'Your feed is empty'}
                  </h3>
                  <p className="text-gray-400 mb-6">
                    {selectedGenres.length > 0 || selectedTypes.length < 3
                      ? 'Try adjusting your filters or reset to see all content'
                      : 'Start following creators to see their latest content here'}
                  </p>
                  {selectedGenres.length > 0 || selectedTypes.length < 3 ? (
                    <Button
                      onClick={resetFilters}
                      variant="outline"
                      className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
                    >
                      See All Content
                    </Button>
                  ) : (
                    <Link to="/browse">
                      <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                        Discover Creators
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {personalizedFeed.map((post, index) => {
                  // Check if this is a creator post
                  if (post.content_source === 'creator_post' || post.feed_type === 'creator_post') {
                    return <PostCard key={post.id} post={post} />;
                  }
                  // Otherwise render as normal feed post
                  return <FeedPost key={post.id} post={post} position={index} />;
                })}
                
                {hasMore && (
                  <div className="text-center py-6">
                    <Button
                      onClick={() => {
                        setPage(page + 1);
                        fetchPersonalizedFeed();
                      }}
                      variant="outline"
                      className="border-gray-800 hover:bg-gray-800"
                    >
                      Load More
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Discovery Feed */}
          <TabsContent value="discover">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : discoveryFeed.length === 0 ? (
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-12 text-center">
                  <Sparkles className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">No posts match your filters</h3>
                  <p className="text-gray-400 mb-6">
                    Try adjusting your genre or content type filters
                  </p>
                  <Button
                    onClick={resetFilters}
                    variant="outline"
                    className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
                  >
                    See All Content
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {discoveryFeed.map((post, index) => {
                  // Check if this is a creator post
                  if (post.content_source === 'creator_post' || post.feed_type === 'creator_post') {
                    return <PostCard key={post.id} post={post} />;
                  }
                  // Otherwise render as normal feed post
                  return <FeedPost key={post.id} post={post} position={index} />;
                })}
                
                <div className="text-center py-6">
                  <Button
                    onClick={() => {
                      setPage(page + 1);
                      fetchDiscoveryFeed();
                    }}
                    variant="outline"
                    className="border-gray-800 hover:bg-gray-800"
                  >
                    Load More
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Feed;
